export const createToggleArtifact = (deps) => {
    return async (artifactId, visible) => {
        const { viewerRef, artifactState, artifactActions, addStatus, reloadArtifact, unloadArtifactFromMemory } = deps;
        const artifact = artifactState.loaded.get(artifactId);
        const currentViewer = viewerRef.current;
        if (visible && !artifact) {
            const cachedData = artifactActions.getMetadataCache(artifactId);
            if (cachedData) {
                const success = await reloadArtifact(artifactId, true);
                if (!success) {
                    console.error(`Failed to reload artifact ${artifactId}`);
                    artifactActions.updateVisibility(artifactId, false);
                }
                return;
            }
            else {
                console.warn(`Cannot show artifact ${artifactId}: not loaded and no cached data`);
                return;
            }
        }
        if (!artifact || !currentViewer)
            return;
        try {
            if (visible) {
                if (artifact.type === 'raster') {
                    artifact.cesiumObject.show = true;
                }
                else if (artifact.type === 'vector') {
                    artifact.cesiumObject.show = true;
                }
                else if (artifact.type === 'placemark') {
                    artifact.cesiumObject.show = true;
                }
                else if (artifact.type === 'screen') {
                    if (!artifactState.activeOverlays.screen.some(overlay => overlay.id === artifactId)) {
                        artifactActions.addScreenOverlay(artifact.cesiumObject);
                    }
                }
                else if (artifact.type === 'info') {
                    if (!artifactState.activeOverlays.info.some(panel => panel.id === artifactId)) {
                        artifactActions.addInfoPanel(artifact.cesiumObject);
                    }
                }
                artifactActions.updateVisibility(artifactId, true);
                addStatus(`Showing ${artifact.metadata.getName()}`);
            }
            else {
                if (artifact.type === 'screen') {
                    artifactActions.removeScreenOverlay(artifactId);
                }
                else if (artifact.type === 'info') {
                    artifactActions.removeInfoPanel(artifactId);
                }
                artifactActions.updateVisibility(artifactId, false);
                unloadArtifactFromMemory(artifactId, artifact);
            }
            if (!visible && artifactState.loading.get(artifactId)) {
                artifactActions.setLoading(artifactId, false);
            }
            setTimeout(() => {
                validateArtifactState(artifactId, deps);
            }, 100);
        }
        catch (error) {
            console.error('Error toggling artifact visibility:', error);
            addStatus(`Error toggling ${artifact.metadata.getName()}: ${error}`);
            artifactActions.updateVisibility(artifactId, false);
        }
    };
};
export const createHideAllArtifacts = (deps) => {
    return () => {
        const { artifactState, addStatus } = deps;
        const toggleArtifact = createToggleArtifact(deps);
        artifactState.loaded.forEach((artifact, artifactId) => {
            toggleArtifact(artifactId, false);
        });
        addStatus('Hidden all artifacts');
    };
};
export const createUpdateArtifactOpacity = (artifactState, artifactActions) => {
    return (artifactId, opacity) => {
        const current = artifactState.controls.get(artifactId) || { opacity: 1.0 };
        artifactActions.updateControls(artifactId, { ...current, opacity });
    };
};
export const createUpdateArtifactWeight = (artifactState, artifactActions) => {
    return (artifactId, weight) => {
        const current = artifactState.controls.get(artifactId) || { opacity: 1.0 };
        artifactActions.updateControls(artifactId, { ...current, weight });
    };
};
export const createUpdateArtifactMonochrome = (_artifactState, artifactActions) => {
    // patchControls merges against the latest state, so restoring opacity and
    // monochrome together (scope apply) doesn't clobber the opacity write.
    return (artifactId, monochrome) => {
        artifactActions.patchControls(artifactId, { monochrome });
    };
};
export const createIsArtifactVisible = (artifactState) => {
    return (artifactId) => {
        return artifactState.visibility.get(artifactId) || false;
    };
};
export const createIsArtifactLoading = (artifactState) => {
    return (artifactId) => {
        return artifactState.loading.get(artifactId) || false;
    };
};
export const validateArtifactState = (artifactId, deps) => {
    const { viewerRef, artifactState, artifactActions, addStatus } = deps;
    const artifact = artifactState.loaded.get(artifactId);
    const storedVisibility = artifactState.visibility.get(artifactId) || false;
    const currentViewer = viewerRef.current;
    if (!artifact || !currentViewer)
        return;
    let actuallyVisible = false;
    try {
        if (artifact.type === 'raster' || artifact.type === 'vector' || artifact.type === 'placemark') {
            actuallyVisible = artifact.cesiumObject && artifact.cesiumObject.show === true;
        }
        else if (artifact.type === 'screen') {
            actuallyVisible = artifactState.activeOverlays.screen.some(overlay => overlay.id === artifactId);
        }
        else if (artifact.type === 'info') {
            actuallyVisible = artifactState.activeOverlays.info.some(panel => panel.id === artifactId);
        }
        if (actuallyVisible !== storedVisibility) {
            console.warn(`State mismatch detected for ${artifact.metadata.getName()}: actual=${actuallyVisible}, stored=${storedVisibility}. Fixing...`);
            artifactActions.updateVisibility(artifactId, actuallyVisible);
            addStatus(`Fixed state sync for ${artifact.metadata.getName()}`);
        }
    }
    catch (error) {
        console.error('Error validating artifact state:', error);
    }
};
export const createValidateArtifactState = (deps) => {
    return (artifactId) => {
        validateArtifactState(artifactId, deps);
    };
};
